import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { getUser, clearAuth } from "@/lib/api";
import { attachGlobalClickSounds } from "@/lib/sounds";
import { List, X, SignOut, UserCircle } from "@phosphor-icons/react";
import BrandLogo from "@/components/BrandLogo";
import RegionToggle from "@/components/RegionToggle";
import NotificationsBell from "@/components/NotificationsBell";
import SoundToggle from "@/components/SoundToggle";

export const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(getUser());
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setUser(getUser());
    setOpen(false);
  }, [location.pathname]);

  // Attach global click-sound listener once at mount.
  useEffect(() => {
    const detach = attachGlobalClickSounds();
    return () => detach();
  }, []);

  const logout = () => {
    clearAuth();
    setUser(null);
    navigate("/");
  };

  const navItems = [
    { to: "/services/transport", label: "Transport & Delivery" },
    { to: "/services/home", label: "Home" },
    { to: "/services/concierge", label: "Concierge" },
    { to: "/services/business", label: "Business" },
    { to: "/become-provider", label: "Become a Provider" },
  ];

  return (
    <div className="App min-h-screen">
      <header
        className="sticky top-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10"
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 group">
            <BrandLogo />
            <span className="hidden md:block h-8 w-px bg-white/10" />
            <span className="hidden md:block text-[10px] tracking-[0.35em] uppercase text-zinc-400">
              Services LLC
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-8">
            {navItems.map(item => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-sm text-zinc-300 hover:text-[#D4AF37] transition-colors duration-200"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <RegionToggle compact />
            <SoundToggle compact />
            {user && <NotificationsBell />}
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link
                    to="/admin"
                    data-testid="header-admin-link"
                    className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-sm text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors"
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to={user.role === "provider" ? "/provider-dashboard" : user.role === "admin" ? "/admin" : "/dashboard"}
                  data-testid="header-dashboard-link"
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 text-sm text-zinc-200 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                >
                  <UserCircle weight="duotone" size={18} />
                  {user.full_name.split(" ")[0]}
                </Link>
                <button
                  onClick={logout}
                  data-testid="logout-button"
                  className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
                >
                  <SignOut size={16} /> Sign out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="header-login-link"
                  data-sound="tock"
                  className="text-sm text-zinc-300 hover:text-white transition-colors"
                >
                  Sign in
                </Link>
                <Link
                  to="/signup"
                  data-testid="header-signup-link"
                  data-sound="tock"
                  className="px-5 py-2 rounded-full bg-[#D4AF37] text-black text-sm font-semibold hover:bg-[#F3E5AB] transition-colors"
                >
                  Get started
                </Link>
              </>
            )}
          </div>

          <div className="lg:hidden flex items-center gap-2">
            <SoundToggle compact />
            <button
              className="text-white"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <List size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div className="lg:hidden border-t border-white/10 bg-black/80 backdrop-blur-xl px-6 py-6 flex flex-col gap-5">
            {navItems.map(item => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className="text-zinc-200 hover:text-[#D4AF37] transition-colors"
              >
                {item.label}
              </Link>
            ))}
            <div className="h-px bg-white/10" />
            {user ? (
              <>
                <Link
                  to={user.role === "provider" ? "/provider-dashboard" : "/dashboard"}
                  className="text-zinc-200"
                >
                  Dashboard
                </Link>
                <button onClick={logout} className="text-left text-zinc-400">Sign out</button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-zinc-200">Sign in</Link>
                <Link
                  to="/signup"
                  className="px-5 py-3 rounded-full bg-[#D4AF37] text-black text-center font-semibold"
                >
                  Get started
                </Link>
              </>
            )}
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="border-t border-white/10 bg-[#09090B] mt-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <BrandLogo />
            </div>
            <p className="text-sm text-zinc-500 leading-relaxed">
              A technology-driven service marketplace. Five verticals. One elite standard.
            </p>
          </div>
          <div>
            <div className="eyebrow mb-4">Services</div>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li><Link to="/services/transport" className="hover:text-[#D4AF37]">Transport &amp; Delivery</Link></li>
              <li><Link to="/services/home" className="hover:text-[#D4AF37]">Home</Link></li>
              <li><Link to="/services/concierge" className="hover:text-[#D4AF37]">Concierge</Link></li>
              <li><Link to="/services/business" className="hover:text-[#D4AF37]">Business</Link></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow mb-4">Company</div>
            <ul className="space-y-2 text-sm text-zinc-400">
              <li><Link to="/about" className="hover:text-[#D4AF37]">About</Link></li>
              <li><Link to="/become-provider" className="hover:text-[#D4AF37]">Become a Provider</Link></li>
              <li><Link to="/contact" className="hover:text-[#D4AF37]">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="eyebrow mb-4">Contact</div>
            <p className="text-sm text-zinc-400">support@ridexelite.com</p>
            <p className="text-sm text-zinc-400">USA: 443-683-6392 (Charles)</p>
            <p className="text-sm text-zinc-400">Liberia: +231 77 768 9123 (Ralph)</p>
          </div>
        </div>
        <div className="border-t border-white/10 py-6 text-center text-xs text-zinc-600">
          © {new Date().getFullYear()} RideXelite Services LLC. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Layout;
